import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import NotificationAlert, { NotificationType } from '../components/shared/NotificationAlert';
import { RENDER_EVERY_FRAME, SPRING, exitTween } from '../components/motion/motionTokens';

// Event types for the notification system
interface NotificationEvent {
  type: NotificationType;
  message: string;
  description?: string;
  duration?: number;
}

interface NotificationState extends NotificationEvent {
  id: number;
}

// Simple event emitter for non-React code (like axiosInterceptor) to trigger notifications
type NotificationListener = (event: NotificationEvent) => void;
const listeners: Set<NotificationListener> = new Set();

export const notificationEmitter = {
  emit: (event: NotificationEvent) => {
    listeners.forEach(listener => listener(event));
  },
  subscribe: (listener: NotificationListener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
};

// One toast. Keeps MUI Snackbar's behavior: auto-hide pauses on hover/focus and while the
// window is blurred (resuming with half the duration); Escape or a click outside it closes it.
// Enters by unblurring down from above, leaves by blurring away, glides when its slot moves.
const NotificationToast: React.FC<{
  notification: NotificationState;
  top: number;
  onClose: (id: number) => void;
}> = ({ notification, top, onClose }) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const autoHideDuration = notification.duration || 4500;
  const { id } = notification;

  const pause = useCallback(() => clearTimeout(timerRef.current), []);
  const startTimer = useCallback((ms: number) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onClose(id), ms);
  }, [id, onClose]);
  const resume = useCallback(() => startTimer(autoHideDuration * 0.5), [autoHideDuration, startTimer]);

  useEffect(() => {
    startTimer(autoHideDuration);
    return pause;
  }, [autoHideDuration, pause, startTimer]);

  useEffect(() => {
    window.addEventListener('focus', resume);
    window.addEventListener('blur', pause);
    return () => {
      window.removeEventListener('focus', resume);
      window.removeEventListener('blur', pause);
    };
  }, [pause, resume]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.defaultPrevented && (event.key === 'Escape' || event.key === 'Esc')) onClose(id);
    };
    // Same click-away rules as MUI ClickAwayListener: armed after mount, ignores touch drags
    // and root-scrollbar clicks.
    let activated = false;
    let moved = false;
    const activateTimer = setTimeout(() => { activated = true; }, 0);
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      const node = nodeRef.current;
      const root = document.documentElement;
      if (!activated || !node) return;
      if ('clientX' in event && (root.clientWidth < event.clientX || root.clientHeight < event.clientY)) return;
      if (moved) {
        moved = false;
        return;
      }
      if (event.composedPath().indexOf(node) === -1) onClose(id);
    };
    const handleTouchMove = () => { moved = true; };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickAway);
    document.addEventListener('touchend', handleClickAway);
    document.addEventListener('touchmove', handleTouchMove);
    return () => {
      clearTimeout(activateTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickAway);
      document.removeEventListener('touchend', handleClickAway);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [id, onClose]);

  return (
    <motion.div
      {...RENDER_EVERY_FRAME}
      ref={nodeRef}
      initial={{ opacity: 0, y: top - 16, scale: 0.94, filter: 'blur(6px)' }}
      animate={{ opacity: 1, y: top, scale: 1, filter: 'blur(0px)' }}
      exit={{ opacity: 0, scale: 0.94, filter: 'blur(6px)', transition: exitTween(0.16) }}
      transition={SPRING.morph}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      style={{ position: 'absolute', top: 0, right: 0, width: 'max-content' }}
    >
      <NotificationAlert
        type={notification.type}
        message={notification.message}
        description={notification.description}
        mode="toast"
        onClose={() => onClose(id)}
      />
    </motion.div>
  );
};

// Context for components that want to show notifications via hook
interface NotificationContextValue {
  showNotification: (type: NotificationType, message: string, description?: string, duration?: number) => void;
  success: (message: string, description?: string) => void;
  error: (message: string, description?: string) => void;
  warning: (message: string, description?: string) => void;
  info: (message: string, description?: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

// Provider component
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const isCompactToast = useMediaQuery(theme.breakpoints.down('sm'));
  const [notifications, setNotifications] = useState<NotificationState[]>([]);

  const showNotification = useCallback((
    type: NotificationType,
    message: string,
    description?: string,
    duration: number = 4500
  ) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message, description, duration }]);
  }, []);

  const handleClose = useCallback((id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const estimateToastStackStep = useCallback((notification: NotificationState) => {
    const baseStep = isCompactToast ? 76 : 68;
    const messageThreshold = isCompactToast ? 42 : 54;
    const descriptionThreshold = isCompactToast ? 72 : 96;
    const messageExtra = notification.message.length > messageThreshold ? isCompactToast ? 18 : 14 : 0;
    const descriptionExtra = notification.description ? (isCompactToast ? 28 : 22) + (notification.description.length > descriptionThreshold ? isCompactToast ? 18 : 14 : 0) : 0;
    return baseStep + messageExtra + descriptionExtra;
  }, [isCompactToast]);

  const getNotificationTop = useCallback((index: number) => {
    const topBase = isCompactToast ? 16 : 24;
    const previousOffset = notifications.slice(0, index).reduce((sum, notification) => sum + estimateToastStackStep(notification), 0);
    return topBase + previousOffset;
  }, [estimateToastStackStep, isCompactToast, notifications]);

  // Subscribe to events from non-React code (notificationService)
  useEffect(() => {
    const unsubscribe = notificationEmitter.subscribe((event) => {
      showNotification(event.type, event.message, event.description, event.duration);
    });
    return unsubscribe;
  }, [showNotification]);

  // Convenience methods
  const success = useCallback((message: string, description?: string) => {
    showNotification('success', message, description);
  }, [showNotification]);

  const error = useCallback((message: string, description?: string) => {
    showNotification('error', message, description);
  }, [showNotification]);

  const warning = useCallback((message: string, description?: string) => {
    showNotification('warning', message, description);
  }, [showNotification]);

  const info = useCallback((message: string, description?: string) => {
    showNotification('info', message, description);
  }, [showNotification]);

  const contextValue: NotificationContextValue = {
    showNotification,
    success,
    error,
    warning,
    info,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {/* Render all active notifications: one fixed top-right anchor; each toast sits at its
          stacked slot (getNotificationTop) and stays mounted until its exit finishes. */}
      <div aria-live="polite" style={{ position: 'fixed', top: 0, right: isCompactToast ? 16 : 24, zIndex: 2000 }}>
        <AnimatePresence>
          {notifications.map((notification, index) => (
            <NotificationToast
              key={notification.id}
              notification={notification}
              top={getNotificationTop(index)}
              onClose={handleClose}
            />
          ))}
        </AnimatePresence>
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationContext;

